import { Box, Button, Group, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as RechartsTootip, XAxis, YAxis } from 'recharts';
import { useChartDatas, useBot, generateCSV, isSameDay } from '@mygpt/app';
import CustomContainer from '@/components/CustomContainer';
import CustomContainerHeader from '@/components/CustomContainer/CustomContainerHeader';
import CustomTooltip from '@/components/Dashboard/Chart/components/CustomTooltip';
import ChartTable from '@/components/Dashboard/ChartTable';
import { SvgFileDownload } from '@/components/Dashboard/icon';
import TitleCard from '@/components/Dashboard/TitleCard';

const AllUsers = () => {
  const { t } = useTranslation();
  const { botId } = useBot();

  const { datas, times, timesText } = useChartDatas();

  const totalNewUsers = useMemo(() => {
    let total = 0;
    datas.map((data) => {
      if (data.new_users) total += data.new_users.length;
    });
    return total;
  }, [datas]);

  const totalOldUsers = useMemo(() => {
    let newUsers: string[] = [];
    const oldUsers: string[] = [];
    // 必现先统计所有新用户，不能放到同一个map
    datas.map((data) => {
      newUsers = newUsers.concat(data.new_users || []);
    });
    datas.map((data) => {
      (data.old_users || []).map((oldUser) => {
        if (!newUsers.includes(oldUser)) {
          oldUsers.push(oldUser);
        }
      });
    });

    return [...new Set(oldUsers)].length;
  }, [datas]);

  const data = useMemo(() => {
    const chartData: { name: string; oldusers: number; newUsers: number }[] = [];
    times.map((item) => {
      let newUsers: string[] = [];
      const oldusers: string[] = [];
      // 需要分开2个map
      datas.map((data) => {
        if (isSameDay(item, data.updated_at)) {
          newUsers = newUsers.concat(data.new_users || []);
        }
      });
      datas.map((data) => {
        if (isSameDay(item, data.updated_at)) {
          (data.old_users || []).map((oldUser) => {
            if (!newUsers.includes(oldUser)) {
              oldusers.push(oldUser);
            }
          });
        }
      });

      const obj = {
        name: item,
        oldusers: oldusers.length,
        newUsers: newUsers.length,
      };
      chartData.push(obj);
    });
    return chartData;
  }, [datas, times]);

  const tableData = useMemo(() => {
    let newUsers: string[] = [];
    const newUsersValue = times.map((time) => {
      let sum = 0;
      datas.map((data) => {
        if (isSameDay(time, data.updated_at) && data.new_users) sum += data.new_users.length;
        newUsers = newUsers.concat(data.new_users || []);
      });
      return {
        key: time,
        value: sum,
      };
    });

    newUsers = [...new Set(newUsers)];
    const oldUsersValue = times.map((time) => {
      let sum = 0;
      datas.map((data) => {
        if (isSameDay(time, data.updated_at) && data.old_users) {
          data.old_users.map((oldUser) => {
            if (!newUsers.includes(oldUser)) {
              sum++;
            }
          });
        }
      });
      return {
        key: time,
        value: sum,
      };
    });

    return [
      {
        type: t('dashboard.card.new_users'),
        value: newUsersValue,
      },
      {
        type: t('dashboard.card.users'),
        value: oldUsersValue,
      },
    ];
  }, [datas]);

  const DownloadButton = () => {
    const download = () => {
      const fileName = `GBase_${botId}_${t('dashboard.card.all_users')}`;
      const header = [t('dashboard.series'), ...times, t('dashboard.total')];
      // 将每个数据对象转换为CSV格式的一行
      const rows = tableData.map((item) => {
        const rowsData = item.value;
        let total = 0;
        const everyCol = rowsData.map((row) => {
          total += row.value;
          return String(row.value);
        });
        return [item.type, ...everyCol, String(total)];
      });

      const data = {
        header,
        rows,
      };
      generateCSV(fileName, data);
    };

    return (
      <Button size="sm" variant="subtle" leftSection={<SvgFileDownload />} onClick={download}>
        <Text style={{ color: '#7C68FB' }}>{t('dashboard.export_csv')}</Text>
      </Button>
    );
  };

  return (
    <>
      <CustomContainerHeader title={t('dashboard.card.all_users')} label={t('dashboard.card.all_users_tip')} />
      <CustomContainer>
        <Stack gap={50}>
          <Group>
            <TitleCard
              title={t('dashboard.card.new_users')}
              valueColor="#FEC84B"
              value={totalNewUsers}
              desc={timesText}
            />
            <TitleCard title={t('dashboard.card.users')} valueColor="#7C68FB" value={totalOldUsers} desc={timesText} />
          </Group>

          <Box w={'100%'} h={350} className="text-sm text-[#667085] font-light">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                width={500}
                height={300}
                data={data}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid stroke="#E4E7EC" strokeDasharray="0 0" vertical={false} />
                <XAxis axisLine={false} tickLine={false} tickMargin={10} padding={{ left: 45 }} dataKey="name" />
                <YAxis axisLine={false} tickLine={false} tickMargin={10} />
                <RechartsTootip
                  content={
                    <CustomTooltip
                      text={{ newUsers: t('dashboard.card.new_users'), oldusers: t('dashboard.card.users') }}
                    />
                  }
                />
                <Line type="monotone" dataKey="newUsers" stroke="#FEC84B" />
                <Line type="monotone" dataKey="oldusers" stroke="#7C68FB" activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          <ChartTable
            title={t('dashboard.table.all_users_title')}
            titleRightSection={<DownloadButton />}
            times={times}
            data={tableData}
          />
        </Stack>
      </CustomContainer>
    </>
  );
};

export default AllUsers;
